import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NavigateFunction } from "react-router-dom";

import type { RollbackReviewPhase } from "../RollbackReviewDialog";
import { buildVersionReviewItems } from "../utils/review-items";

import {
  useGetSkillCollectionVersion,
  useRestoreSkillCollectionVersion,
} from "@/queries/skill-collections";
import type { SkillCollectionVersionRecord } from "@/queries/skill-collections/types";

const ROLLBACK_REDIRECT_DELAY_MS = 900;

interface UseSkillCollectionRollbackParams {
  workspaceIdNum: number | null;
  collectionIdStr: string | null;
  basePath: string | null;
  navigate: NavigateFunction;
  versions: SkillCollectionVersionRecord[];
}

export const useSkillCollectionRollback = ({
  workspaceIdNum,
  collectionIdStr,
  basePath,
  navigate,
  versions,
}: UseSkillCollectionRollbackParams) => {
  const [rollbackTargetVersion, setRollbackTargetVersion] =
    useState<SkillCollectionVersionRecord | null>(null);
  const [rollbackPhase, setRollbackPhase] = useState<RollbackReviewPhase>("review");
  const rollbackSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoreMutation = useRestoreSkillCollectionVersion();

  const latestVersionNumber = versions[0]?.number ?? 1;
  const rollbackTargetVersionNumber = rollbackTargetVersion?.number ?? null;
  const rollbackSourceVersionNumber = rollbackTargetVersion ? latestVersionNumber : null;

  const {
    data: rollbackSourceVersionData,
    isLoading: isLoadingRollbackSourceVersion,
    error: rollbackSourceVersionError,
  } = useGetSkillCollectionVersion(workspaceIdNum, collectionIdStr, rollbackSourceVersionNumber);
  const {
    data: rollbackTargetVersionData,
    isLoading: isLoadingRollbackTargetVersion,
    error: rollbackTargetVersionError,
  } = useGetSkillCollectionVersion(workspaceIdNum, collectionIdStr, rollbackTargetVersionNumber);

  const rollbackReviewItems = useMemo(() => {
    if (!rollbackSourceVersionData?.files || !rollbackTargetVersionData?.files) {
      return [];
    }

    return buildVersionReviewItems({
      previousFiles: rollbackSourceVersionData.files,
      nextFiles: rollbackTargetVersionData.files,
      previousProvider: rollbackSourceVersionData.version.provider,
      nextProvider: rollbackTargetVersionData.version.provider,
      previousFilePaths: rollbackSourceVersionData.version.file_paths,
      nextFilePaths: rollbackTargetVersionData.version.file_paths,
    });
  }, [
    rollbackSourceVersionData?.files,
    rollbackSourceVersionData?.version.provider,
    rollbackSourceVersionData?.version.file_paths,
    rollbackTargetVersionData?.files,
    rollbackTargetVersionData?.version.provider,
    rollbackTargetVersionData?.version.file_paths,
  ]);

  const isRollbackReviewLoading =
    rollbackTargetVersion !== null &&
    (isLoadingRollbackSourceVersion ||
      isLoadingRollbackTargetVersion ||
      (!rollbackSourceVersionData?.files && !rollbackSourceVersionError) ||
      (!rollbackTargetVersionData?.files && !rollbackTargetVersionError));

  const rollbackReviewEmptyMessage =
    rollbackSourceVersionError || rollbackTargetVersionError
      ? "Unable to load the selected version diff."
      : "No changes to review.";

  const resetRollbackPhase = useCallback(() => {
    if (rollbackSuccessTimeoutRef.current) {
      clearTimeout(rollbackSuccessTimeoutRef.current);
      rollbackSuccessTimeoutRef.current = null;
    }
    setRollbackPhase("review");
  }, []);

  useEffect(
    () => () => {
      if (rollbackSuccessTimeoutRef.current) {
        clearTimeout(rollbackSuccessTimeoutRef.current);
      }
    },
    [],
  );

  const handleRequestRollback = useCallback(
    (version: SkillCollectionVersionRecord) => {
      if (restoreMutation.isPending || version.id === versions[0]?.id) {
        return;
      }

      resetRollbackPhase();
      setRollbackTargetVersion(version);
    },
    [resetRollbackPhase, restoreMutation.isPending, versions],
  );

  const handleCloseRollbackReview = useCallback(() => {
    if (rollbackPhase !== "review") {
      return;
    }

    resetRollbackPhase();
    setRollbackTargetVersion(null);
  }, [resetRollbackPhase, rollbackPhase]);

  const handleConfirmRollback = useCallback(() => {
    if (!rollbackTargetVersion || !workspaceIdNum || !collectionIdStr || !basePath) {
      return;
    }

    setRollbackPhase("saving");
    restoreMutation.mutate(
      {
        workspaceId: workspaceIdNum,
        collectionId: collectionIdStr,
        versionId: rollbackTargetVersion.id,
      },
      {
        onSuccess: (response: { version: { number: number } }) => {
          setRollbackPhase("success");
          rollbackSuccessTimeoutRef.current = setTimeout(() => {
            setRollbackTargetVersion(null);
            resetRollbackPhase();
            navigate(`${basePath}/version/${response.version.number}`);
          }, ROLLBACK_REDIRECT_DELAY_MS);
        },
        onError: () => {
          resetRollbackPhase();
        },
      },
    );
  }, [
    basePath,
    collectionIdStr,
    navigate,
    resetRollbackPhase,
    restoreMutation,
    rollbackTargetVersion,
    workspaceIdNum,
  ]);

  return {
    rollbackTargetVersion,
    rollbackPhase,
    rollbackReviewItems,
    isRollbackReviewLoading,
    rollbackReviewEmptyMessage,
    restoreMutation,
    handleRequestRollback,
    handleCloseRollbackReview,
    handleConfirmRollback,
  };
};
